import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * An array of all the viewport types, in ascending order of size.
 */
export const viewportTypes = ["s", "m", "l", "xl", "xxl", "xxxl"] as const;

// Viewport sizes
export type ViewportType = typeof viewportTypes[number];

/**
 * A type that represents a string value that has a different value for each viewport type.
 * The keys of the object are the viewport types, and the values are the corresponding values for each viewport type.
 */
export type PerViewport<T> = { readonly [key in ViewportType]: T };

/**
 * A type that represents a string value that has a different value for each viewport type, with a default value.
 * The keys of the object are the viewport types, and the values are the corresponding values for each viewport type.
 * The `default` key is used to specify the default value for all viewport types.
 */
export type PerViewportWithDefault<T> = { readonly default: T } & Partial<PerViewport<T>>;

interface ViewportContextType {
    readonly viewportType: ViewportType;
}

/**
 * A context object that provides the current viewport type to its descendants.
 * The context value is an object that contains the current viewport type and a function to update the viewport type.
 */
export const ViewportContext = createContext<ViewportContextType | null>(null);

type ViewportBounds = PerViewport<number>;
export type ViewportBoundsFlexible<T extends string> = { readonly [key in T]: number };

/**
 * An object that contains the minimum widths for each viewport type.
 * The keys of the object are the viewport types, and the values are the corresponding minimum widths for each viewport type.
 */
export const viewportWidthMin: ViewportBounds = {
    s: 0,
    m: 480,
    l: 640,
    xl: 1024,
    xxl: 1366,
    xxxl: 1920,
};

/**
 * An object that contains the maximum widths for each viewport type.
 * The keys of the object are the viewport types, and the values are the corresponding maximum widths for each viewport type.
 * Subtracting 1 from each maximum width ensures that the media query for each viewport type does not overlap with the media query for the next larger viewport type.
 */
export const viewportWidthMax: ViewportBounds = {
    s: viewportWidthMin.m - 1,
    m: viewportWidthMin.l - 1,
    l: viewportWidthMin.xl - 1,
    xl: viewportWidthMin.xxl - 1,
    xxl: viewportWidthMin.xxxl - 1,
    xxxl: Number.MAX_SAFE_INTEGER,
};

/**
 * A function that returns the viewport type that corresponds to a given pixel width.
 * The function takes a pixel width as input, and returns the viewport type that has a maximum width greater than or equal to the input width.
 * If no viewport type has a maximum width greater than or equal to the input width, the function returns "xxxl".
 * @param pixelWidth The pixel width to get the corresponding viewport type for.
 * @param viewportWidthMaxDict The dictionary values of the max widths for the designated viewport type
 * @param viewportTypesArray The viewport types array that will be used to determine the current viewport type.
 * @returns The viewport type that corresponds to the given pixel width.
 */
export function viewportTypeFromPixelWidthGeneric<T extends string>(
    pixelWidth: number,
    viewportWidthMaxDict: ViewportBoundsFlexible<T>,
    viewportTypesArray: readonly T[]
): T {
    return viewportTypesArray.find(v => pixelWidth <= viewportWidthMaxDict[v]) || viewportTypesArray[viewportTypesArray.length - 1];
}

/**
 * A hook that returns the current viewport type, based on the width of the viewport.
 * @returns The current viewport type
 */
export const useViewportProvider = (): ViewportType => {
    const context = useContext(ViewportContext);

    if (context === null) {
        throw new Error("ViewportContext: Context is uninitialized. Confirm that your component is contained within an ViewportContext.Provider component.");
    }

    return context.viewportType;
};

/**
 * A hook to watch the viewport and return the current viewport type that corresponds to the page width.
 * The function takes a pixel width as input, and returns the viewport type that has a maximum width greater than or equal to the input width.
 * If no viewport type has a maximum width greater than or equal to the input width, the function returns "xxxl".
 * @param viewportTypesArray The viewport types array that will be used to determine the current viewport type.
 * @param widthMin The dictionary of min viewport widths for each viewport type.
 * @param widthMax The dictionary of max viewport widths for each viewport type.
 * @returns The viewport type that corresponds to the window width.
 */
export function useWatchViewport<T extends string>(
    viewportTypesArray: readonly T[],
    widthMin: ViewportBoundsFlexible<T>,
    widthMax: ViewportBoundsFlexible<T>,
): T {

    const [viewportType, setViewportType] = useState<T>(
        viewportTypeFromPixelWidthGeneric(
            window.innerWidth,
            widthMax,
            viewportTypesArray
        )
    );

    const mediaToViewportType: { [key: string]: T } = {};

    const updateViewportWidth = (event: MediaQueryListEvent): void => {
        if (event.matches) {
            const newViewportType = mediaToViewportType[event.media] ||
                viewportTypeFromPixelWidthGeneric(
                    window.innerWidth,
                    widthMax,
                    viewportTypesArray
                );
            setViewportType(newViewportType);
        }
    };

    useEffect(() => {
        // Create media queries for each viewport. If window.matchMedia doesn't exist, such as in jsdom, don't initialize media queries
        const mediaMatchers = window.matchMedia ? viewportTypesArray.map(v => {
            const matcher = window.matchMedia(`(max-width: ${widthMax[v]}px) and (min-width: ${widthMin[v]}px)`);
            mediaToViewportType[matcher.media] = v;
            return matcher;
        }) : undefined;

        // Former body of useWatchViewport
        if (mediaMatchers) {
            mediaMatchers.forEach((m) => {
                if (m !== undefined && m.addEventListener !== undefined) {
                    m.addEventListener("change", updateViewportWidth);
                }
            });
        }
        setViewportType(viewportTypeFromPixelWidthGeneric(
            window.innerWidth,
            widthMax,
            viewportTypesArray
        ));

        return () => {
            if (mediaMatchers) {
                // don't forget to clean up
                mediaMatchers.forEach((m) => {
                    if (m !== undefined && m.removeEventListener !== undefined) {
                        m.removeEventListener("change", updateViewportWidth);
                    }
                });
            }
        };
    }, []);

    return viewportType;
}

/**
 * A component that provides the current viewport type to its descendants using a context object.
 * The component uses the `useViewportProvider` hook to get the current viewport type, and updates the context value whenever the viewport type changes.
 * @param children The child elements that will receive the current viewport type from the context object.
 * @returns The `ViewportContext.Provider` component, which provides the current viewport type to its descendants.
 */
export const ViewportProvider: React.FC = ({
    children
}) => {
    const viewportType = useWatchViewport<ViewportType>(
        viewportTypes,
        viewportWidthMin,
        viewportWidthMax
    );

    return (
        <ViewportContext.Provider value={{ viewportType }}>
            {children}
        </ViewportContext.Provider>
    );
};
